import { resolveAPIBaseURLCandidates } from "@/lib/api";
import { readStoredAuthSession } from "@/lib/auth-storage";
import {
  classifyFrontendError,
  type FrontendErrorCategory,
  type FrontendErrorDiagnostic,
  type FrontendErrorSeverity,
} from "@/lib/frontend-error-diagnostics";

type FrontendErrorSource =
  | "global_error"
  | "unhandled_rejection"
  | "resource_error"
  | "react_boundary"
  | "router_boundary"
  | "api_error"
  | "manual";

type FrontendErrorReportInput = {
  source: FrontendErrorSource;
  error: unknown;
  componentStack?: string;
  extra?: Record<string, unknown>;
};

type FrontendErrorFrame = {
  raw: string;
  function_name: string;
  url: string;
  line: number;
  column: number;
};

type FrontendErrorAssets = {
  document_ready_state: string;
  visibility_state: string;
  active_element: string;
  scripts: string[];
  stylesheets: string[];
  storage: {
    local_storage_available: boolean;
    session_storage_available: boolean;
    local_storage_keys: string[];
    session_storage_keys: string[];
  };
};

export type FrontendErrorReport = {
  id: string;
  source: FrontendErrorSource;
  timestamp: string;
  category: FrontendErrorCategory;
  code: string;
  severity: FrontendErrorSeverity;
  name: string;
  message: string;
  stack: string;
  component_stack: string;
  diagnostic: FrontendErrorDiagnostic;
  frames: FrontendErrorFrame[];
  url: string;
  path: string;
  route: {
    pathname: string;
    search: string;
    hash_present: boolean;
  };
  user_agent: string;
  build: Record<string, string>;
  viewport: {
    width: number;
    height: number;
    device_pixel_ratio: number;
  };
  assets: FrontendErrorAssets;
  extra: Record<string, unknown>;
};

declare global {
  interface Window {
    __GENRL_FRONTEND_ERRORS__?: FrontendErrorReport[];
    __GENRL_LAST_FRONTEND_ERROR_ID__?: string;
  }
}

const FRONTEND_ERROR_ENDPOINT = "/api/v1/frontend/errors";
const LOCAL_ERROR_LOG_KEY = "genrl.frontend-errors.v1";
const MAX_LOCAL_ERROR_REPORTS = 50;
const MAX_TEXT_LENGTH = 12000;

let installed = false;
const recentReports = new Map<string, number>();

function truncate(value: unknown, maxLength = MAX_TEXT_LENGTH) {
  const text = String(value ?? "");
  return text.length > maxLength ? `${text.slice(0, maxLength)}...` : text;
}

function serializeUnknown(value: unknown) {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") return value;
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

function normalizeError(error: unknown) {
  if (error instanceof Error) {
    return {
      name: truncate(error.name || "Error", 256),
      message: truncate(error.message || "Unknown frontend error", 2048),
      stack: truncate(error.stack || ""),
    };
  }
  const serialized = serializeUnknown(error);
  return {
    name: "Error",
    message: truncate(serialized || "Unknown frontend error", 2048),
    stack: "",
  };
}

function errorReportID() {
  try {
    return crypto.randomUUID();
  } catch {
    return `fe_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
  }
}

function safeLocation() {
  if (typeof window === "undefined")
    return { url: "", path: "", pathname: "", search: "", hashPresent: false };
  try {
    const url = new URL(window.location.href);
    for (const key of Array.from(url.searchParams.keys())) {
      if (/token|secret|password|code|state|session|auth|key/i.test(key)) {
        url.searchParams.set(key, "redacted");
      }
    }
    url.hash = "";
    return {
      url: url.toString(),
      path: `${url.pathname}${url.search}`,
      pathname: url.pathname,
      search: url.search,
      hashPresent: Boolean(window.location.hash),
    };
  } catch {
    return {
      url: truncate(window.location.href || "", 2048),
      path: truncate(`${window.location.pathname || ""}${window.location.search || ""}`, 2048),
      pathname: truncate(window.location.pathname || "", 2048),
      search: truncate(window.location.search || "", 2048),
      hashPresent: Boolean(window.location.hash),
    };
  }
}

function safeExtra(extra?: Record<string, unknown>) {
  if (!extra) return {};
  try {
    return JSON.parse(truncate(JSON.stringify(extra), 4096)) as Record<string, unknown>;
  } catch {
    return { value: truncate(serializeUnknown(extra), 4096) };
  }
}

function stackFrames(stack: string): FrontendErrorFrame[] {
  return String(stack || "")
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean)
    .slice(0, 12)
    .map((raw) => {
      const match =
        raw.match(/at\s+(.*?)\s+\((.*?):(\d+):(\d+)\)$/) || raw.match(/at\s+(.*?):(\d+):(\d+)$/);
      if (!match) {
        return { raw: truncate(raw, 1024), function_name: "", url: "", line: 0, column: 0 };
      }
      if (match.length === 5) {
        return {
          raw: truncate(raw, 1024),
          function_name: truncate(match[1] || "", 256),
          url: truncate(match[2] || "", 2048),
          line: Number(match[3] || 0),
          column: Number(match[4] || 0),
        };
      }
      return {
        raw: truncate(raw, 1024),
        function_name: "",
        url: truncate(match[1] || "", 2048),
        line: Number(match[2] || 0),
        column: Number(match[3] || 0),
      };
    });
}

function storageSnapshot(storage: Storage | undefined) {
  if (!storage) return { available: false, keys: [] as string[] };
  try {
    const keys: string[] = [];
    for (let index = 0; index < storage.length; index += 1) {
      const key = storage.key(index);
      if (!key) continue;
      keys.push(
        /token|secret|password|code|state|session|auth|key/i.test(key)
          ? "redacted"
          : truncate(key, 256),
      );
      if (keys.length >= 40) break;
    }
    return { available: true, keys };
  } catch {
    return { available: false, keys: [] as string[] };
  }
}

function browserStorage(kind: "localStorage" | "sessionStorage") {
  if (typeof window === "undefined") return undefined;
  try {
    return window[kind];
  } catch {
    return undefined;
  }
}

function elementLabel(element: Element | null | undefined) {
  if (!element) return "";
  const id = element.id ? `#${element.id}` : "";
  const testID = element.getAttribute("data-testid");
  const role = element.getAttribute("role");
  const label = element.getAttribute("aria-label");
  return truncate(
    [
      element.tagName.toLowerCase(),
      id,
      testID ? `[data-testid="${testID}"]` : "",
      role ? `[role="${role}"]` : "",
      label ? `[aria-label="${label}"]` : "",
    ].join(""),
    512,
  );
}

function collectAssets(): FrontendErrorAssets {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return {
      document_ready_state: "",
      visibility_state: "",
      active_element: "",
      scripts: [],
      stylesheets: [],
      storage: {
        local_storage_available: false,
        session_storage_available: false,
        local_storage_keys: [],
        session_storage_keys: [],
      },
    };
  }
  const local = storageSnapshot(browserStorage("localStorage"));
  const session = storageSnapshot(browserStorage("sessionStorage"));
  return {
    document_ready_state: truncate(document.readyState || "", 128),
    visibility_state: truncate(document.visibilityState || "", 128),
    active_element: elementLabel(document.activeElement),
    scripts: Array.from(document.scripts)
      .map((script) => truncate(script.src || "", 2048))
      .filter(Boolean)
      .slice(-30),
    stylesheets: Array.from(document.querySelectorAll<HTMLLinkElement>('link[rel~="stylesheet"]'))
      .map((link) => truncate(link.href || "", 2048))
      .filter(Boolean)
      .slice(-30),
    storage: {
      local_storage_available: local.available,
      session_storage_available: session.available,
      local_storage_keys: local.keys,
      session_storage_keys: session.keys,
    },
  };
}

function createReport(input: FrontendErrorReportInput): FrontendErrorReport {
  const normalized = normalizeError(input.error);
  const location = safeLocation();
  const diagnostic = classifyFrontendError(input.error, {
    source: input.source,
    componentStack: input.componentStack,
    extra: input.extra,
  });
  return {
    id: errorReportID(),
    source: input.source,
    timestamp: new Date().toISOString(),
    category: diagnostic.category,
    code: diagnostic.code,
    severity: diagnostic.severity,
    name: normalized.name,
    message: normalized.message,
    stack: normalized.stack,
    component_stack: truncate(input.componentStack || ""),
    diagnostic,
    frames: stackFrames(normalized.stack),
    url: location.url,
    path: location.path,
    route: {
      pathname: location.pathname,
      search: location.search,
      hash_present: location.hashPresent,
    },
    user_agent: typeof navigator === "undefined" ? "" : truncate(navigator.userAgent || "", 1024),
    build: {
      mode: truncate(import.meta.env.MODE || "", 128),
      app_version: truncate(import.meta.env.VITE_APP_VERSION || "", 128),
      commit: truncate(
        import.meta.env.VITE_VERCEL_GIT_COMMIT_SHA || import.meta.env.VITE_GIT_COMMIT_SHA || "",
        128,
      ),
    },
    viewport: {
      width: typeof window === "undefined" ? 0 : Math.round(window.innerWidth || 0),
      height: typeof window === "undefined" ? 0 : Math.round(window.innerHeight || 0),
      device_pixel_ratio: typeof window === "undefined" ? 0 : Number(window.devicePixelRatio || 0),
    },
    assets: collectAssets(),
    extra: safeExtra(input.extra),
  };
}

function reportFingerprint(report: FrontendErrorReport) {
  return [report.source, report.path, report.name, report.message, report.stack.slice(0, 240)].join(
    "|",
  );
}

function storeLocalReport(report: FrontendErrorReport) {
  if (typeof window === "undefined") return;
  try {
    const existing = JSON.parse(
      window.localStorage.getItem(LOCAL_ERROR_LOG_KEY) || "[]",
    ) as FrontendErrorReport[];
    const next = [...(Array.isArray(existing) ? existing : []), report].slice(
      -MAX_LOCAL_ERROR_REPORTS,
    );
    window.localStorage.setItem(LOCAL_ERROR_LOG_KEY, JSON.stringify(next));
    window.__GENRL_FRONTEND_ERRORS__ = next;
  } catch {
    window.__GENRL_FRONTEND_ERRORS__ = [...(window.__GENRL_FRONTEND_ERRORS__ || []), report].slice(
      -MAX_LOCAL_ERROR_REPORTS,
    );
  }
}

function reportURL(baseURL: string) {
  const base = String(baseURL || "")
    .trim()
    .replace(/\/$/, "");
  return `${base}${FRONTEND_ERROR_ENDPOINT}`;
}

async function sendRemoteReport(report: FrontendErrorReport) {
  if (typeof window === "undefined") return;
  const body = JSON.stringify(report);
  const session = readStoredAuthSession();
  const headers: Record<string, string> = {
    Accept: "application/json",
    "Content-Type": "application/json",
  };
  if (session?.token) headers.Authorization = `Bearer ${session.token}`;

  const seen = new Set<string>();
  const candidates = resolveAPIBaseURLCandidates().filter((baseURL) => {
    const url = reportURL(baseURL);
    if (seen.has(url)) return false;
    seen.add(url);
    return true;
  });

  for (const baseURL of candidates) {
    try {
      const response = await fetch(reportURL(baseURL), {
        method: "POST",
        headers,
        body,
        credentials: "include",
        keepalive: body.length < 60_000,
      });
      if (response.ok) return;
      if (response.status !== 404 && response.status !== 405) return;
    } catch {
      // Error reporting is best-effort and must never create a second user-visible failure.
    }
  }
}

export function reportFrontendError(input: FrontendErrorReportInput): FrontendErrorReport {
  const report = createReport(input);
  const fingerprint = reportFingerprint(report);
  const now = Date.now();
  const lastSeenAt = recentReports.get(fingerprint) || 0;
  recentReports.set(fingerprint, now);

  storeLocalReport(report);
  if (typeof window !== "undefined") {
    window.__GENRL_LAST_FRONTEND_ERROR_ID__ = report.id;
  }
  console.error(`[frontend:${input.source}:${report.code}] ${report.message}`, {
    id: report.id,
    name: report.name,
    category: report.category,
    severity: report.severity,
    diagnostic: report.diagnostic,
    stack: report.stack,
    frames: report.frames,
    componentStack: report.component_stack,
    path: report.path,
    route: report.route,
    assets: report.assets,
    extra: report.extra,
  });

  if (now - lastSeenAt < 5000) return report;
  void sendRemoteReport(report);
  return report;
}

export function installGlobalBrowserErrorHandlers() {
  if (installed || typeof window === "undefined") return;
  installed = true;

  window.addEventListener(
    "error",
    (event) => {
      const target = event.target;
      if (target instanceof HTMLElement && target.tagName) {
        const resource =
          target instanceof HTMLLinkElement
            ? target.href
            : target instanceof HTMLScriptElement || target instanceof HTMLImageElement
              ? target.src
              : "";
        reportFrontendError({
          source: "resource_error",
          error: new Error(`Resource failed to load: ${resource || target.tagName.toLowerCase()}`),
          extra: {
            tag_name: target.tagName.toLowerCase(),
            resource_url: resource,
          },
        });
        return;
      }
      reportFrontendError({
        source: "global_error",
        error: event.error || event.message,
        extra: {
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno,
        },
      });
    },
    true,
  );

  window.addEventListener("unhandledrejection", (event) => {
    reportFrontendError({
      source: "unhandled_rejection",
      error: event.reason,
    });
  });

  window.addEventListener("genrl:api-error", (event) => {
    const detail = (
      event as CustomEvent<{
        error?: unknown;
        path?: string;
        method?: string;
        status?: number;
        code?: string;
      }>
    ).detail;
    reportFrontendError({
      source: "api_error",
      error: detail?.error || new Error("API request failed"),
      extra: {
        path: detail?.path || "",
        method: detail?.method || "",
        status: Number(detail?.status || 0),
        code: detail?.code || "",
      },
    });
  });
}
